import { useEffect, useRef, useState } from "react";
import { Mail, MapPin, ArrowUpRight, Loader2, Check, Phone, Linkedin } from "lucide-react";
import { toast, Toaster } from "sonner";
import { gsap, ScrollTrigger } from "../lib/gsap";
import Breadcrumbs from "../components/site/Breadcrumbs";

const WEB3FORMS_ACCESS_KEY = "b4b9518b-9aa5-4212-addc-ef24bef30a10";
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const INTERESTS = [
  "Capital Raising",
  "M&A / Advisory",
  "Project Finance",
  "Restructuring",
  "Strategic Partnership",
  "Other",
];

export default function Contact() {
  const root = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: "Capital Raising",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".js-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { y: 60, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.1, ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" } }
        );
      });
    }, root);

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const body = new FormData();
      body.append("access_key", WEB3FORMS_ACCESS_KEY);
      body.append("from_name", "GICP Website");
      body.append("subject", `GICP Inquiry — ${form.interest} — ${form.name}`);
      body.append("name", form.name);
      body.append("email", form.email);
      body.append("company", form.company || "—");
      body.append("interest", form.interest);
      body.append("message", form.message);
      body.append("replyto", form.email);
      body.append("botcheck", "");

      const res = await fetch(WEB3FORMS_ENDPOINT, { method: "POST", body });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || `Web3Forms error ${res.status}`);
      }
      setDone(true);
      toast.success("Inquiry received. Our team will reply within 1 business day.");
      setForm({ name: "", email: "", company: "", interest: "Capital Raising", message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.message || "Network error";
      toast.error(
        `Couldn't send right now — email us directly at investor@gicp.co.za (${detail})`,
        { duration: 8000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const inputBase = "w-full rounded-md border bg-transparent px-4 py-3.5 text-base placeholder:opacity-50 transition-colors duration-300 focus:border-[var(--gicp-gold)] focus:ring-2 focus:ring-[var(--gicp-gold)]/15";

  return (
    <div ref={root} data-testid="contact-page">
      <Toaster richColors position="top-center" />
      {/* HERO */}
      <section className="relative pt-0 pb-24 md:pb-32 min-h-[70vh] md:min-h-[80vh] overflow-hidden flex items-end">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_motion-web-studio-1/artifacts/0nvmf9ct_AdobeStock_2065114477.jpeg"
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "saturate(0.95) brightness(0.92) contrast(1.05)", objectPosition: "left center" }}
          />
          {/* Right-side dark wash so text is readable */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,9,20,0.15) 0%, rgba(5,9,20,0.30) 35%, rgba(5,9,20,0.75) 65%, rgba(5,9,20,0.92) 100%)" }} />
          {/* Top fade for the navbar */}
          <div className="absolute inset-x-0 top-0 h-48" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.55) 0%, transparent 100%)" }} />
          {/* Bottom fade into bg */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, transparent 60%, rgba(5,9,20,0.6) 90%, var(--bg) 100%)" }} />
        </div>
        <div className="container-x relative z-10 w-full">
          <Breadcrumbs />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end mt-12 md:mt-20">
            <div className="md:col-span-5 md:col-start-7 js-reveal">
              <span className="overline" style={{ color: "#FDFBF7" }}>— Contact &amp; Governance</span>
              <h1 className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] mt-6 leading-[0.92] tracking-tighter" style={{ color: "#FDFBF7" }}>
                Start a<br/>
                <em className="italic" style={{ color: "var(--gicp-gold)" }}>conversation</em>.
              </h1>
              <p className="mt-10 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "rgba(253,251,247,0.80)" }}>
                Confidential. Direct. We respond to every inquiry within one business day —
                from mandates and targets to thesis-driven introductions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT LAYOUT */}
      <section className="section pt-12">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          {/* Left: info */}
          <aside className="md:col-span-5 js-reveal">
            <div className="space-y-12">
              <div>
                <span className="overline">— Office</span>
                <h3 className="font-serif text-2xl md:text-3xl mt-4 flex items-start gap-4">
                  <MapPin size={22} className="mt-2" style={{ color: "var(--gicp-gold)" }} />
                  <span>
                    1 Amand Avenue,<br/>
                    Menlyn Maine, Pretoria,<br/>
                    0181, South Africa
                  </span>
                </h3>
              </div>

              <div>
                <span className="overline">— Inquiries</span>
                <a
                  href="mailto:investor@gicp.co.za"
                  data-testid="contact-email"
                  className="font-serif text-2xl md:text-3xl mt-4 flex items-center gap-4 hover:text-[var(--gicp-gold)] transition-colors"
                >
                  <Mail size={22} style={{ color: "var(--gicp-gold)" }} />
                  investor@gicp.co.za
                </a>
                <a
                  href="tel:+27630206195"
                  data-testid="contact-phone"
                  className="font-serif text-2xl md:text-3xl mt-4 flex items-center gap-4 hover:text-[var(--gicp-gold)] transition-colors"
                >
                  <Phone size={22} style={{ color: "var(--gicp-gold)" }} />
                  +27 63 020 6195
                </a>
                <a
                  href="https://za.linkedin.com/in/simon-teffo-5686b31b3"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin"
                  className="font-serif text-2xl md:text-3xl mt-4 flex items-center gap-4 hover:text-[var(--gicp-gold)] transition-colors"
                >
                  <Linkedin size={22} style={{ color: "var(--gicp-gold)" }} />
                  LinkedIn
                </a>
              </div>

              <div className="pt-8 border-t" style={{ borderColor: "var(--border-subtle)" }}>
                <span className="overline">— Why Partner</span>
                <ul className="mt-6 space-y-5">
                  {[
                    { t: "Execution Focus", d: "We don't just advise — we close complex transactions." },
                    { t: "Capital Access", d: "We connect the right projects to the right capital." },
                    { t: "Integrity", d: "Transparency is the bedrock of every relationship." },
                  ].map((x, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="text-xs pt-1 tracking-[0.3em]" style={{ color: "var(--gicp-gold)" }}>0{i + 1}</span>
                      <div>
                        <div className="text-base">{x.t}</div>
                        <p className="text-sm mt-1" style={{ color: "var(--fg-soft)" }}>{x.d}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>

          {/* Right: form */}
          <div className="md:col-span-7 js-reveal">
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-6 p-6 md:p-10 rounded-lg border" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-soft)" }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div className="space-y-2">
                  <label className="text-[11px] tracking-[0.25em] uppercase font-medium" htmlFor="cf-name" style={{ color: "var(--fg-soft)" }}>Name</label>
                  <input
                    id="cf-name"
                    data-testid="contact-input-name"
                    className={inputBase}
                    placeholder="Full name"
                    value={form.name}
                    onChange={onChange("name")}
                    style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] tracking-[0.25em] uppercase font-medium" htmlFor="cf-email" style={{ color: "var(--fg-soft)" }}>Email</label>
                  <input
                    id="cf-email"
                    data-testid="contact-input-email"
                    type="email"
                    className={inputBase}
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={onChange("email")}
                    style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div className="space-y-2">
                  <label className="text-[11px] tracking-[0.25em] uppercase font-medium" htmlFor="cf-company" style={{ color: "var(--fg-soft)" }}>Company</label>
                  <input
                    id="cf-company"
                    data-testid="contact-input-company"
                    className={inputBase}
                    placeholder="Organisation"
                    value={form.company}
                    onChange={onChange("company")}
                    style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] tracking-[0.25em] uppercase font-medium" htmlFor="cf-interest" style={{ color: "var(--fg-soft)" }}>Interest</label>
                  <select
                    id="cf-interest"
                    data-testid="contact-input-interest"
                    className={inputBase + " appearance-none cursor-pointer pr-10"}
                    value={form.interest}
                    onChange={onChange("interest")}
                    style={{ borderColor: "var(--border-subtle)", color: "var(--fg)", backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23999'><path fill-rule='evenodd' d='M10 12a1 1 0 01-.7-.3l-4-4a1 1 0 011.4-1.4L10 9.6l3.3-3.3a1 1 0 011.4 1.4l-4 4a1 1 0 01-.7.3z' clip-rule='evenodd'/></svg>\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0.8rem center", backgroundSize: "16px" }}
                  >
                    {INTERESTS.map((opt) => (
                      <option key={opt} value={opt} style={{ background: "var(--bg)", color: "var(--fg)" }}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] tracking-[0.25em] uppercase font-medium" htmlFor="cf-message" style={{ color: "var(--fg-soft)" }}>Brief</label>
                <textarea
                  id="cf-message"
                  data-testid="contact-input-message"
                  rows={6}
                  className={inputBase + " resize-none"}
                  placeholder="Tell us about your mandate, target, or thesis..."
                  value={form.message}
                  onChange={onChange("message")}
                  style={{ borderColor: "var(--border-subtle)", color: "var(--fg)" }}
                  required
                />
              </div>

              <div className="pt-2 flex items-center gap-6 flex-wrap">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  className="btn-gold disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending
                    </>
                  ) : done ? (
                    <>
                      <Check size={16} /> Sent
                    </>
                  ) : (
                    <>
                      Send inquiry <ArrowUpRight size={16} />
                    </>
                  )}
                </button>
                <span className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "var(--fg-soft)" }}>
                  Confidential · Replies within 1 business day
                </span>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
